import { useEffect, useRef } from 'react';
import {
  BarChart3, CalendarCheck2, CheckSquare, LayoutDashboard, Share2, Sparkles,
  SquareKanban, Users,
} from 'lucide-react';
import { SectionHeader } from '../SectionHeader';
import { Reveal } from '../Reveal';
import { trackEvent } from '@/lib/analytics';

const FEATURE_ICONS = {
  'My Dashboard': LayoutDashboard,
  'Sales Pipeline': SquareKanban,
  'Customers': Users,
  'Activities': CheckSquare,
  'Referrals': Share2,
  'Planner': CalendarCheck2,
  'Ask Mylo': Sparkles,
  'Reports': BarChart3,
};

// Section 4 — core platform capabilities.
export const ReachCapabilities = ({ sp }) => {
  const ref = useRef(null);
  const seen = useRef(false);

  useEffect(() => {
    if (!ref.current || seen.current) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !seen.current) {
          seen.current = true;
          trackEvent('reach_module_viewed');
          obs.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} aria-labelledby="reach-capabilities-heading" data-testid="reach-capabilities" className="bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="04"
          eyebrow="Platform Capabilities"
          title="Everything a working sales day needs"
          description="Reach connects the book of business each salesperson owns with the visibility management needs — not a contact list with a reporting bolt-on."
          testid="reach-capabilities-header"
        />
        <ul className="mt-14 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
          {(sp.features || []).map((feature, i) => {
            const Icon = FEATURE_ICONS[feature.title] || LayoutDashboard;
            return (
              <Reveal key={feature.title} delay={(i % 4) * 0.05}>
                <li className="flex items-start gap-4" data-testid={`reach-feature-${i + 1}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-service-growth text-white">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="text-base font-extrabold tracking-tight text-navy-900">{feature.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{feature.text}</p>
                  </div>
                </li>
              </Reveal>
            );
          })}
        </ul>
      </div>
    </section>
  );
};
